import React from "react";
import {ThunderboltOutlined, WarningOutlined} from "@ant-design/icons";
import {Button, DragAndDrop, Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import SectionCard from "../../common/sectionCard/SectionCard";
import {MAX_FILE_SIZE_MB, VERIFY_LATENCY_NOTE} from "../uploads-simulator.constants";
import styles from "../styles/upload-run.module.scss";

/**
 * Dropzone + run affordance. The `DragAndDrop` accept list is widened beyond
 * its images-only default to include `application/pdf` (per-mode config), and
 * `key={dndKey}` remounts it when the tab changes so stale files never leak
 * across modes. The min-file rule for the full manual flow is OUR validation
 * (the server accepts a single file to demo the insufficient-contiguity path).
 *
 * @param {Object} props
 * @param {Object} props.modeConfig — entry from `MODES`
 * @param {File[]} props.files
 * @param {number} props.dndKey — bumped by the controller to reset the zone
 * @param {string[]} [props.validationMessages] — pre-computed blocking issues
 * @param {boolean} props.canRun — all validation green + role allows it
 * @param {boolean} props.running
 * @param {boolean} [props.canSimulate] — role gate (button hidden copy when false)
 * @param {(entries: {file: File}[]) => void} props.onFilesChange
 * @param {() => void} props.onRun
 */
function UploadRunPanel({modeConfig, files, dndKey, validationMessages = [], canRun, running, canSimulate = true, onFilesChange, onRun}) {
    const fileCount = files?.length || 0;

    return (
        <SectionCard icon={<ThunderboltOutlined />} title="Documents & Run">
            <div className={styles.uploadStack}>
                <DragAndDrop
                    key={dndKey}
                    className={styles.dropzone}
                    accept={[...modeConfig.accept]}
                    multiple={modeConfig.multiple}
                    maxFiles={modeConfig.maxFiles}
                    maxSizeMB={MAX_FILE_SIZE_MB}
                    label={modeConfig.dropLabel}
                    onChange={onFilesChange}
                />

                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.hint}>
                    {modeConfig.hint}
                </Text>

                {validationMessages.length ? (
                    <div className={styles.validationSummary}>
                        {validationMessages.map((message, index) => (
                            <Text key={index} as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.validationLine}>
                                <WarningOutlined className={styles.validationIcon} />
                                {message}
                            </Text>
                        ))}
                    </div>
                ) : null}

                <div className={styles.runRow}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.fileCount}>
                        {fileCount} of {modeConfig.maxFiles} file{modeConfig.maxFiles === 1 ? "" : "s"} selected
                    </Text>
                    <Button className={styles.runButton} disabled={!canRun || running} loading={running} onClick={onRun}>
                        {running ? "Running…" : "Run simulation"}
                    </Button>
                </div>
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.latencyNote}>
                    {canSimulate ? VERIFY_LATENCY_NOTE : "Only Owners can run simulations."}
                </Text>
            </div>
        </SectionCard>
    );
}

export default UploadRunPanel;
